// WHO WRITES each `TaskFailureKind` — one seam declaration per kind, projected from
// its row (`./failure-kinds/`). See `../task.ts` for the file docblock.

import { projectFailureKinds, type TaskFailureKind } from './failure-kind';
import type { TaskFailureSeam } from './failure-kinds/row';

// The seam vocabulary lives beside the rows that are written in it (`./failure-kinds/row.ts`).
export { TaskFailureSeam, TaskFailureSeamSchema } from './failure-kinds/row';

export type TaskFailureProducer =
  | {
      readonly seam: typeof TaskFailureSeam.Wire;
      /**
       * EVERY throw site that stamps the code, as repo-relative `path#Symbol`.
       * A list, not a string, because a kind genuinely can have more than one —
       * `git_auth` is stamped by four separate throw sites, and naming one of
       * them would have made the others invisible to the next grep. (This
       * sentence used to cite `tool_unavailable`'s two producers; #3164 split
       * that pair apart precisely because they needed different budgets, so it
       * is no longer an example of one kind legitimately having several.) Each entry is
       * checked against its file, so a producer DELETED goes red even when a
       * sibling survives.
       *
       * "EVERY" IS NOW CHECKED IN BOTH DIRECTIONS, and it was a claim nobody
       * could check until 2026-08-29. The forward half — every declared anchor
       * really stamps its code — shipped with the table and holds. The reverse
       * half did not exist, so a throw site that stamped a code and told nobody
       * was invisible by exactly the mechanism this table was built to end, and
       * four of them had accumulated: `team/native.ts` (`checkout_unavailable`),
       * `pr-branch.ts` and `worktree-store.ts` (`git_auth`), `babysit.ts`
       * (`model_produced_no_work`). `task.test.ts` now scans `apps` +
       * `packages` for `code: '<kind>'` assignments and fails on any file this
       * list does not name.
       */
      readonly producedBy: readonly string[];
    }
  | {
      readonly seam: typeof TaskFailureSeam.WireUnproduced;
      /** What is missing, and how that was established. A marker with no reason is the silence it replaces. */
      readonly why: string;
    }
  | {
      readonly seam: typeof TaskFailureSeam.Platform;
      /** Every lane that writes the column, as repo-relative `path#Symbol`. */
      readonly producedBy: readonly string[];
    };

/**
 * ONE PRODUCER DECLARATION PER FAILURE KIND — the partition `git_auth`
 * cost us.
 *
 * `git_auth` shipped with a budget, a wire code, a paragraph of documentation
 * and NO producer, then sat at zero rows for a whole slice while prod carried a
 * live population of exactly the failure it names. Nothing went red, because an
 * unreachable kind is indistinguishable from a kind that never happens — both
 * are an empty `GROUP BY`. Fourteen kinds existed and 1,028 of 1,029 classified
 * rows landed in two of them.
 *
 * Projected from each kind's row (`./failure-kinds/`), like {@link TASK_FAILURE_BUDGET}:
 * `FailureKindRow.producer` is required, so a new {@link TaskFailureKind} fails
 * typecheck at its row until someone names its writer or states that it has none. And {@link TASK_FAILURE_WIRE_CODE} is DERIVED from
 * this table rather than hand-listed beside it, so "has a code" and "has a
 * producer" cannot drift apart — which is precisely how a code came to exist for
 * a kind nothing raised.
 *
 * The `wire` arm is checked, not believed: `task.test.ts` reads each named file
 * and asserts it stamps the code. A declaration that is merely intended is the
 * bug, not the fix.
 *
 * WHAT THE CHECK CANNOT DO, and it matters: it proves a stamp EXISTS in the named
 * file, never that the file RUNS. Three of these kinds were first pointed at
 * `apps/runner/src/adapter/run-program.ts#runProgram`, and a stamp there would have
 * turned the guard green while never once executing — on `main` that function
 * spawns no coding agent at all (its callers run `bin/check`, the verify gate,
 * deep review and CI exec), and all 503 `command` events in prod come from the
 * DELETED spawn lane: `/opt/dz-runner/adapter.sh` 318, `/home/hvadmin/
 * aitm-adapter.sh` 147, `dz-agent` 39. It was caught by querying prod for which
 * `cmd` values actually appear, which is a check no grep can make. So: the anchor
 * must name a seam that runs, and "it compiles and the guard is green" does not
 * establish that.
 */
export const TASK_FAILURE_PRODUCER: Record<TaskFailureKind, TaskFailureProducer> =
  projectFailureKinds((row) => row.producer);
